

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('login'); // 'login' or 'forgot-password'
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const navigate = useNavigate();

  const config = {
    domain: "farpoint.us.auth0.com",
    webClientId: "kDrr9cGuOqVO28GYl8x05hroSYgpGTdH",
    audience: "https://farpoint.us.auth0.com/api/v2/"
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Both email and password fields are required.");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const tokenData = new URLSearchParams({
        grant_type: "http://auth0.com/oauth/grant-type/password-realm",
        username: email,
        password: password,
        audience: config.audience,
        client_id: config.webClientId,
        realm: "Username-Password-Authentication",
        scope: "openid profile email",
      });

      const response = await fetch(`https://${config.domain}/oauth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json",
        },
        body: tokenData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error_description || "Login failed");
      }

      const userResponse = await fetch(`https://${config.domain}/userinfo`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${data.access_token}`,
        },
      });

      const userData = await userResponse.json();

      // Fetch user_metadata from Management API (has the real username)
      let username = userData.name || userData.email;
      try {
        const mgmtResponse = await fetch(
          `https://${config.domain}/api/v2/users/${encodeURIComponent(userData.sub)}`,
          { headers: { Authorization: `Bearer ${data.access_token}` } }
        );
        if (mgmtResponse.ok) {
          const mgmtData = await mgmtResponse.json();
          username = mgmtData.user_metadata?.username || userData.name || userData.email;
        }
      } catch (err) {
        console.warn("Could not fetch user metadata:", err);
      }

      const userInfo = {
        username,
        email: userData.email,
      };

      sessionStorage.setItem("user", JSON.stringify(userInfo));
      sessionStorage.setItem("access_token", data.access_token);

      console.log("User info saved:", userInfo);

      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      setError(error.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address to reset password.");
      return;
    }

    setResetLoading(true);
    setError('');
    setResetSuccess(false);

    try {
      const response = await fetch(
        `https://${config.domain}/dbconnections/change_password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: config.webClientId,
            email: email,
            connection: 'Username-Password-Authentication',
          }),
        }
      );

      if (response.ok) {
        setResetSuccess(true);
      } else {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || data.description || "Failed to send reset link");
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setError(err.message || "Failed to send password reset email. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center mb-6">
            <img src="/logo.png" alt="Optimus Logo" className="w-72 h-auto" />
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-8 py-10">
          {/* Header */}
          <div className="mb-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              ADMIN
            </p>
            <h2 className="text-2xl font-normal text-gray-900" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              {view === 'forgot-password' ? 'Reset your password' : 'Log in to your account'}
            </h2>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                {error}
              </p>
            </div>
          )}

          {/* Success Message for Reset */}
          {resetSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-sm" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                A password reset link has been sent to <strong>{email}</strong>. Please check your inbox.
              </p>
            </div>
          )}

          {view === 'forgot-password' ? (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label className="block text-sm text-gray-600 mb-2" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Email address
                </label>
                <input
                  type="email"
                  placeholder="elon@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full bg-[#1A5090] text-white py-3 px-4 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-900 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {resetLoading ? 'Sending...' : 'Send reset link'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setView('login');
                    setError('');
                    setResetSuccess(false);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  Back to Login
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-2" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Email or username
                </label>
                <input
                  type="text"
                  placeholder="elon@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  required
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-2" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="text-right mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setView('forgot-password');
                      setError('');
                      setResetSuccess(false);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1A5090] text-white py-3 px-4 rounded-lg text-sm font-medium  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-900 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
